import React, { useEffect, useMemo, useRef, useState } from "react";
import EventCard from "../components/EventCard";
import EventFlyer from "../components/EventFlyer";
import {
  COMMUNITY_INFORMATION_AGENT_ENABLED,
  COMMUNITY_INFORMATION_PROVIDER_LABEL,
} from "./config";
import { communityInformationService } from "./service";
import "./communityInformation.css";

const EDITABLE_DRAFT_FIELDS = [
  "title",
  "description",
  "organiserName",
  "venueName",
  "address",
  "postcode",
  "startDate",
  "endDate",
  "startTime",
  "endTime",
  "timezone",
  "rawDateTimeText",
  "recurrenceText",
  "priceAmount",
  "priceCurrency",
  "priceText",
  "audience",
  "ageRestrictions",
  "bookingMethod",
  "bookingUrl",
  "accessibility",
  "publicContact",
];

const EMPTY_DRAFT = {
  title: "",
  description: "",
  organiserName: "",
  venueName: "",
  address: "",
  postcode: "",
  startDate: "",
  endDate: "",
  startTime: "",
  endTime: "",
  timezone: "",
  rawDateTimeText: "",
  recurrenceText: "",
  priceAmount: "",
  priceCurrency: "",
  priceText: "",
  audience: "",
  ageRestrictions: "",
  bookingMethod: "",
  bookingUrl: "",
  accessibility: "",
  publicContact: "",
  suggestedTags: "",
};

const DRAFT_FIELDS = [
  { name: "title", label: "Title", wide: true },
  { name: "organiserName", label: "Organiser" },
  { name: "description", label: "Plain-language description", multiline: true, wide: true },
  { name: "venueName", label: "Venue" },
  { name: "address", label: "Address" },
  { name: "postcode", label: "Postcode" },
  { name: "publicContact", label: "Public organiser contact" },
  { name: "startDate", label: "Start date", placeholder: "YYYY-MM-DD or leave unknown" },
  { name: "endDate", label: "End date", placeholder: "YYYY-MM-DD or leave unknown" },
  { name: "startTime", label: "Start time", placeholder: "HH:MM or leave unknown" },
  { name: "endTime", label: "End time", placeholder: "HH:MM or leave unknown" },
  { name: "timezone", label: "Timezone" },
  { name: "rawDateTimeText", label: "Original date and time wording", multiline: true },
  { name: "recurrenceText", label: "Recurrence wording", multiline: true },
  { name: "priceAmount", label: "Price amount", inputMode: "decimal" },
  { name: "priceCurrency", label: "Price currency", placeholder: "GBP or leave unknown" },
  { name: "priceText", label: "Original price wording" },
  { name: "audience", label: "Audience", multiline: true },
  { name: "ageRestrictions", label: "Age restrictions" },
  { name: "bookingMethod", label: "Booking method" },
  { name: "bookingUrl", label: "Booking URL" },
  { name: "accessibility", label: "Accessibility information", multiline: true, wide: true },
  { name: "suggestedTags", label: "Suggested tags (comma-separated)", wide: true },
];

function stringValue(value) {
  if (Array.isArray(value)) return value.join(", ");
  return value === undefined || value === null ? "" : String(value);
}

function draftFromReview(review = {}) {
  const proposed = review.draft || {};
  return {
    ...Object.fromEntries(
      EDITABLE_DRAFT_FIELDS.map((field) => [field, stringValue(proposed[field])])
    ),
    suggestedTags: stringValue(proposed.suggestedTags),
  };
}

function resultReview(result = {}, fallback = {}) {
  const received = result.review || result;
  const merged = { ...fallback, ...received };
  const receivedDraft = received.draft || fallback.draft || {};
  return {
    ...merged,
    reviewId: merged.reviewId || merged.id || fallback.reviewId || "",
    expectedVersion:
      merged.expectedVersion ??
      merged.reviewVersion ??
      merged.version ??
      fallback.expectedVersion,
    processingState:
      merged.processingState || merged.state || fallback.processingState || "needs_review",
    draft: receivedDraft,
    evidence:
      received.evidence ?? receivedDraft.evidence ?? fallback.evidence ?? [],
    missingFields:
      received.missingFields ?? receivedDraft.missingFields ?? fallback.missingFields ?? [],
    conflictingFields:
      received.conflictingFields ??
      received.conflicts ??
      receivedDraft.conflictingFields ??
      fallback.conflictingFields ??
      [],
    warnings:
      received.warnings ?? receivedDraft.warnings ?? fallback.warnings ?? [],
    publicationBlockers:
      received.publicationBlockers ??
      received.blockers ??
      fallback.publicationBlockers ??
      [],
    duplicateSuggestions:
      received.duplicateSuggestions ??
      received.duplicates ??
      fallback.duplicateSuggestions ??
      [],
    clarificationMessage:
      received.clarificationMessage ??
      receivedDraft.clarificationMessage ??
      fallback.clarificationMessage ??
      "",
    clarificationQuestions:
      received.clarificationQuestions ??
      receivedDraft.clarificationQuestions ??
      fallback.clarificationQuestions ??
      [],
  };
}

function splitSuggestedTags(value) {
  return String(value || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, 12);
}

function draftForSave(editableDraft, review = {}) {
  const preserved = review.draft || {};
  return {
    schemaVersion: preserved.schemaVersion || "1.0",
    ...Object.fromEntries(
      EDITABLE_DRAFT_FIELDS.map((field) => [field, editableDraft[field]])
    ),
    suggestedTags: splitSuggestedTags(editableDraft.suggestedTags),
    evidence: review.evidence ?? preserved.evidence ?? [],
    missingFields: review.missingFields ?? preserved.missingFields ?? [],
    conflictingFields:
      review.conflictingFields ?? preserved.conflictingFields ?? [],
    warnings: review.warnings ?? preserved.warnings ?? [],
    clarificationQuestions:
      review.clarificationQuestions ?? preserved.clarificationQuestions ?? [],
    clarificationMessage:
      review.clarificationMessage ?? preserved.clarificationMessage ?? "",
  };
}

function createIdempotencyKey() {
  if (
    typeof window !== "undefined" &&
    typeof window.crypto?.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }
  return `uun-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function issueTitle(issue) {
  if (typeof issue === "string") return issue;
  return issue.field || issue.title || issue.name || "Review item";
}

function issueDetail(issue) {
  if (!issue || typeof issue === "string") return "";
  return (
    issue.reason ||
    issue.message ||
    issue.details ||
    issue.description ||
    issue.values?.join?.(" compared with ") ||
    ""
  );
}

function duplicateReason(duplicate) {
  if (duplicate?.reason) return duplicate.reason;
  if (!Array.isArray(duplicate?.reasons)) {
    return "Potential match suggested for administrator review.";
  }
  const reasons = duplicate.reasons
    .map((reason) =>
      typeof reason === "string"
        ? reason
        : reason?.message || reason?.reason || reason?.type || ""
    )
    .filter(Boolean);
  return reasons.join("; ") || "Potential match suggested for administrator review.";
}

function IssuePanel({ title, items, tone, emptyText }) {
  return (
    <section className={`uun-ci-issue uun-ci-issue-${tone}`}>
      <h3>{title}</h3>
      {items.length ? (
        <ul>
          {items.map((item, index) => (
            <li key={`${issueTitle(item)}-${index}`}>
              <strong>{issueTitle(item)}</strong>
              {issueDetail(item) ? <span>{issueDetail(item)}</span> : null}
            </li>
          ))}
        </ul>
      ) : (
        <p>{emptyText}</p>
      )}
    </section>
  );
}

function formatSourceDate(value) {
  if (!value) return "";
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/London",
  }).format(date);
}

export function originalEventFrom(review, preparedSource) {
  const original = (
    review?.originalEvent ||
    review?.source?.event ||
    (preparedSource?.sourceType === "event_submission" ? preparedSource.event : null)
  );
  if (!original) return null;
  return {
    ...original,
    id:
      original.id ||
      review?.source?.submissionId ||
      preparedSource?.event?.id ||
      preparedSource?.submissionId ||
      "",
  };
}

export async function copyClarificationMessage(message, clipboard) {
  if (!message) throw new Error("There is no clarification message to copy.");
  if (!clipboard || typeof clipboard.writeText !== "function") {
    throw new Error("Clipboard access is unavailable. Select and copy the message manually.");
  }
  await clipboard.writeText(message);
}

export default function CommunityInformationAdmin({
  enabled = COMMUNITY_INFORMATION_AGENT_ENABLED,
  service = communityInformationService,
}) {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [sourceType, setSourceType] = useState("event_submission");
  const [submissionId, setSubmissionId] = useState("");
  const [partnerText, setPartnerText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [preparedSource, setPreparedSource] = useState(null);
  const [review, setReview] = useState(null);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [savedFingerprint, setSavedFingerprint] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [confirmApproval, setConfirmApproval] = useState(false);
  const busyActionRef = useRef("");
  const partnerIntakeRef = useRef({ fingerprint: "", key: "" });
  const prepareAttemptRef = useRef({ fingerprint: "", failed: false });

  useEffect(() => {
    if (!enabled) return undefined;
    setEventsLoading(true);
    setEventsError("");
    return service.subscribeCommunityInformationEvents(
      (items) => {
        setEvents(items);
        setSubmissionId((current) => current || items[0]?.id || "");
        setEventsLoading(false);
      },
      () => {
        setEventsError(
          "Pending event submissions could not be loaded. You can still paste partner text or continue with manual moderation."
        );
        setEventsLoading(false);
      }
    );
  }, [enabled, service]);

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === submissionId) || null,
    [events, submissionId]
  );
  const draftFingerprint = JSON.stringify(draft);
  const dirty = Boolean(review && draftFingerprint !== savedFingerprint);
  const blockers = review?.publicationBlockers || [];
  const approved = review?.processingState === "approved" || review?.status === "approved";
  const approvalReady = review?.processingState === "needs_review";
  const originalEvent = originalEventFrom(review, preparedSource);
  const originalText =
    review?.originalText ||
    review?.source?.text ||
    (preparedSource?.sourceType === "partner_text" ? preparedSource.partnerText : "");
  const previewEvent = {
    ...(originalEvent || {}),
    ...draft,
    id: "",
    sourceId: originalEvent?.id || originalEvent?.sourceId || "",
    title: draft.title,
    description: draft.description,
    organiserName: draft.organiserName,
    venueName: draft.venueName,
    contact: draft.publicContact,
    startDate: draft.startDate,
    endDate: draft.endDate,
    startTime: draft.startTime,
    endTime: draft.endTime,
    cost: draft.priceText,
    bookingUrl: draft.bookingUrl,
  };

  if (!enabled) return null;

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const runAction = async (name, action) => {
    if (busyActionRef.current) return null;
    busyActionRef.current = name;
    clearMessages();
    setBusyAction(name);
    try {
      return await action();
    } catch (actionError) {
      setError(
        actionError?.message ||
          "The Community Information request failed. The original submission is unchanged and manual moderation remains available."
      );
      return null;
    } finally {
      busyActionRef.current = "";
      setBusyAction("");
    }
  };

  const prepareDraft = async () => {
    if (review) {
      setError(
        dirty
          ? "Save or abandon the current edited draft before preparing another source."
          : "Abandon the current review before preparing another source. This prevents a prepared draft being silently replaced."
      );
      return;
    }

    const partnerFingerprint = JSON.stringify([partnerText, sourceUrl]);
    if (
      sourceType === "partner_text" &&
      partnerIntakeRef.current.fingerprint !== partnerFingerprint
    ) {
      partnerIntakeRef.current = {
        fingerprint: partnerFingerprint,
        key: createIdempotencyKey(),
      };
    }
    const idempotencyKey =
      sourceType === "partner_text" ? partnerIntakeRef.current.key : undefined;
    const sourceFingerprint =
      sourceType === "event_submission"
        ? `event_submission:${submissionId}`
        : `partner_text:${partnerFingerprint}`;
    if (prepareAttemptRef.current.fingerprint !== sourceFingerprint) {
      prepareAttemptRef.current = { fingerprint: sourceFingerprint, failed: false };
    }
    const retry = prepareAttemptRef.current.failed;
    const source =
      sourceType === "event_submission"
        ? { sourceType, submissionId, event: selectedEvent }
        : { sourceType, partnerText, sourceUrl, idempotencyKey };
    const result = await runAction("prepare", () =>
      service.prepareCommunityEventDraft({
        sourceType,
        submissionId,
        partnerText,
        sourceUrl,
        idempotencyKey,
        retry,
      })
    );
    if (!result) {
      prepareAttemptRef.current = { fingerprint: sourceFingerprint, failed: true };
      return;
    }

    const nextReview = resultReview(result);
    if (nextReview.processingState === "failed") {
      prepareAttemptRef.current = { fingerprint: sourceFingerprint, failed: true };
      setError(
        nextReview.failure?.message ||
          "Draft preparation failed. The original source is unchanged; retry or continue with manual moderation."
      );
      return;
    }
    prepareAttemptRef.current = { fingerprint: sourceFingerprint, failed: false };
    const nextDraft = draftFromReview(nextReview);
    setPreparedSource(source);
    setReview(nextReview);
    setDraft(nextDraft);
    setSavedFingerprint(JSON.stringify(nextDraft));
    setSuccess(
      "Draft prepared for review. Nothing has been published and the original source is unchanged."
    );
  };

  const saveDraft = async () => {
    if (!review?.reviewId) return;
    const currentDraft = { ...draft };
    const strictDraft = draftForSave(currentDraft, review);
    const result = await runAction("save", () =>
      service.saveCommunityEventDraft({
        reviewId: review.reviewId,
        expectedVersion: review.expectedVersion,
        draft: strictDraft,
      })
    );
    if (!result) return;

    setReview((current) => resultReview(result, current));
    setSavedFingerprint(JSON.stringify(currentDraft));
    setConfirmApproval(false);
    setSuccess("Draft saved and rechecked. It is still private and unpublished.");
  };

  const markNeedsInformation = async () => {
    if (!review?.reviewId || dirty) {
      setError("Save the current edits before marking this review as needing information.");
      return;
    }
    if (!review.clarificationMessage) {
      setError("There is no clarification message to attach to this review.");
      return;
    }
    const result = await runAction("needs-information", () =>
      service.markCommunityEventNeedsInformation({
        reviewId: review.reviewId,
        expectedVersion: review.expectedVersion,
        clarificationMessage: review.clarificationMessage,
      })
    );
    if (!result) return;
    setReview((current) => ({
      ...resultReview(result, current),
      processingState: "needs_information",
    }));
    setSuccess(
      "Marked as needing information. No message was sent; copy the draft if you want to contact the organiser."
    );
  };

  const rejectDraft = async () => {
    if (!review?.reviewId) return;
    const result = await runAction("reject", () =>
      service.rejectCommunityEventDraft({
        reviewId: review.reviewId,
        expectedVersion: review.expectedVersion,
        reason: "Administrator abandoned the generated suggestion.",
      })
    );
    if (!result) return;
    setReview(null);
    setDraft({ ...EMPTY_DRAFT });
    setSavedFingerprint("");
    setPreparedSource(null);
    setConfirmApproval(false);
    setSuccess(
      "Suggestion rejected. The original submission remains available for normal manual moderation."
    );
  };

  const approveDraft = async () => {
    if (!review?.reviewId || !approvalReady || dirty || blockers.length) return;
    const result = await runAction("approve", () =>
      service.approveCommunityEventDraft({
        reviewId: review.reviewId,
        expectedVersion: review.expectedVersion,
      })
    );
    if (!result) return;
    setReview((current) => ({
      ...resultReview(result, current),
      processingState: "approved",
    }));
    setConfirmApproval(false);
    setSuccess("Approved and published through the existing event workflow.");
  };

  const copyMessage = async () => {
    clearMessages();
    try {
      await copyClarificationMessage(
        review?.clarificationMessage,
        typeof navigator === "undefined" ? null : navigator.clipboard
      );
      setSuccess("Clarification message copied. Nothing was sent automatically.");
    } catch (copyError) {
      setError(copyError.message);
    }
  };

  return (
    <section className="uun-ci" aria-labelledby="uun-community-information-title">
      <header className="uun-ci-header">
        <div>
          <p className="uun-ci-kicker">Administrator workspace</p>
          <h1 id="uun-community-information-title">Community information</h1>
          <p>
            Prepare an evidence-backed event draft, review every change, then publish only with explicit approval.
          </p>
        </div>
        <div className="uun-ci-provider" role="status">
          <span aria-hidden="true" />
          {COMMUNITY_INFORMATION_PROVIDER_LABEL}
        </div>
      </header>

      <div className="uun-ci-safety-note">
        AI preparation is optional. It never sends messages or publishes on its own, and normal manual moderation remains available.
        <a href="/admin">Continue with manual moderation</a>
      </div>

      {error ? <div className="uun-ci-alert uun-ci-alert-error" role="alert">{error}</div> : null}
      {success ? <div className="uun-ci-alert uun-ci-alert-success" role="status">{success}</div> : null}

      <section className="uun-ci-card" aria-labelledby="uun-ci-source-title">
        <div className="uun-ci-section-heading">
          <div>
            <p className="uun-ci-step">Step 1</p>
            <h2 id="uun-ci-source-title">Choose the original source</h2>
          </div>
          {eventsLoading ? <span className="uun-ci-muted" role="status">Loading submissions…</span> : null}
        </div>

        <div className="uun-ci-source-tabs" role="radiogroup" aria-label="Event source">
          <label>
            <input
              type="radio"
              name="community-information-source"
              value="event_submission"
              checked={sourceType === "event_submission"}
              onChange={() => setSourceType("event_submission")}
              disabled={Boolean(review)}
            />
            Existing pending event
          </label>
          <label>
            <input
              type="radio"
              name="community-information-source"
              value="partner_text"
              checked={sourceType === "partner_text"}
              onChange={() => setSourceType("partner_text")}
              disabled={Boolean(review)}
            />
            Pasted partner text
          </label>
        </div>

        {eventsError ? <p className="uun-ci-inline-error" role="status">{eventsError}</p> : null}

        {sourceType === "event_submission" ? (
          <label className="uun-ci-field">
            <span>Pending submission</span>
            <select
              value={submissionId}
              onChange={(event) => setSubmissionId(event.target.value)}
              disabled={Boolean(review) || eventsLoading}
            >
              <option value="">Choose an event</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title || event.name || "Untitled event"} — {event.id} — {event.status || "pending"}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="uun-ci-form-grid">
            <label className="uun-ci-field uun-ci-wide">
              <span>Partner event information</span>
              <textarea
                value={partnerText}
                onChange={(event) => setPartnerText(event.target.value)}
                rows="8"
                maxLength="12000"
                disabled={Boolean(review)}
                placeholder="Paste the supplied event or activity information exactly as received."
              />
            </label>
            <label className="uun-ci-field uun-ci-wide">
              <span>Source URL (attribution only, optional)</span>
              <input
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                maxLength="2000"
                disabled={Boolean(review)}
                placeholder="https://example.org/source"
              />
              <small>This phase does not fetch or verify the linked page.</small>
            </label>
          </div>
        )}

        {!review ? (
          <button
            type="button"
            className="uun-ci-button uun-ci-button-primary"
            onClick={prepareDraft}
            disabled={
              Boolean(busyAction) ||
              (sourceType === "event_submission" ? !submissionId : !partnerText.trim())
            }
          >
            {busyAction === "prepare" ? "Preparing draft…" : "Prepare draft"}
          </button>
        ) : null}
      </section>

      {review ? (
        <>
          <div className="uun-ci-review-grid">
            <section className="uun-ci-card" data-testid="original-source" aria-labelledby="uun-ci-original-title">
              <div className="uun-ci-section-heading">
                <div>
                  <p className="uun-ci-step">Original</p>
                  <h2 id="uun-ci-original-title">Source kept unchanged</h2>
                </div>
                <span className="uun-ci-state">{review.processingState.replace(/_/g, " ")}</span>
              </div>
              {originalEvent ? (
                <>
                  <dl className="uun-ci-original-details">
                    <div><dt>Submission</dt><dd>{originalEvent.id}</dd></div>
                    <div><dt>Status</dt><dd>{originalEvent.status || "pending"}</dd></div>
                    <div><dt>Title</dt><dd>{originalEvent.title || originalEvent.name || "Untitled"}</dd></div>
                    <div><dt>Received</dt><dd>{formatSourceDate(originalEvent.createdAt) || "Date unavailable"}</dd></div>
                  </dl>
                  {originalEvent.description ? <p className="uun-ci-original-copy">{originalEvent.description}</p> : null}
                  <EventFlyer event={originalEvent} compact showFallback={false} className="uun-ci-original-flyer" eager />
                </>
              ) : (
                <pre className="uun-ci-original-text">{originalText}</pre>
              )}
              <p className="uun-ci-muted">The source and original flyer remain separate from this editable proposal.</p>
            </section>

            <section className="uun-ci-card" aria-labelledby="uun-ci-evidence-title">
              <div className="uun-ci-section-heading">
                <div>
                  <p className="uun-ci-step">Evidence</p>
                  <h2 id="uun-ci-evidence-title">Why the draft says this</h2>
                </div>
              </div>
              {review.evidence.length ? (
                <ul className="uun-ci-evidence-list">
                  {review.evidence.map((item, index) => (
                    <li key={`${item.field || item.fact || "evidence"}-${index}`}>
                      <strong>{item.field || item.fact || "Draft field"}</strong>
                      <p>{item.excerpt || item.evidence || item.imageLocation || "Evidence location recorded"}</p>
                      <small>
                        Source: {item.sourceId || "supplied source"}
                        {item.sourceType === "image" || item.imageLocation ? " · Extracted from image — awaiting review" : ""}
                      </small>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="uun-ci-muted">No evidence references were returned. Treat this as a review blocker.</p>
              )}
            </section>
          </div>

          <section className="uun-ci-card" aria-labelledby="uun-ci-draft-title">
            <div className="uun-ci-section-heading">
              <div>
                <p className="uun-ci-step">Step 2</p>
                <h2 id="uun-ci-draft-title">Edit the proposed public event</h2>
              </div>
              {dirty ? <span className="uun-ci-unsaved">Unsaved edits</span> : <span className="uun-ci-saved">Saved</span>}
            </div>
            <div className="uun-ci-form-grid">
              {DRAFT_FIELDS.map((field) => (
                <label key={field.name} className={`uun-ci-field ${field.wide ? "uun-ci-wide" : ""}`}>
                  <span>{field.label}</span>
                  {field.multiline ? (
                    <textarea
                      value={draft[field.name]}
                      onChange={(event) => {
                        setDraft((current) => ({ ...current, [field.name]: event.target.value }));
                        setConfirmApproval(false);
                        clearMessages();
                      }}
                      rows={field.wide ? 4 : 3}
                      disabled={approved}
                    />
                  ) : (
                    <input
                      value={draft[field.name]}
                      onChange={(event) => {
                        setDraft((current) => ({ ...current, [field.name]: event.target.value }));
                        setConfirmApproval(false);
                        clearMessages();
                      }}
                      inputMode={field.inputMode}
                      placeholder={field.placeholder || "Unknown — leave blank"}
                      disabled={approved}
                    />
                  )}
                </label>
              ))}
            </div>
            {!approved ? (
              <button
                type="button"
                className="uun-ci-button uun-ci-button-secondary"
                onClick={saveDraft}
                disabled={Boolean(busyAction) || !dirty}
              >
                {busyAction === "save" ? "Saving and checking…" : "Save draft"}
              </button>
            ) : null}
          </section>

          <section className="uun-ci-card" aria-labelledby="uun-ci-checks-title">
            <div className="uun-ci-section-heading">
              <div>
                <p className="uun-ci-step">Step 3</p>
                <h2 id="uun-ci-checks-title">Resolve checks before publication</h2>
              </div>
            </div>
            <div className="uun-ci-issues-grid">
              <IssuePanel title="Publication blockers" items={blockers} tone="blocker" emptyText="No publication blockers reported." />
              <IssuePanel title="Missing information" items={review.missingFields} tone="missing" emptyText="No missing fields reported." />
              <IssuePanel title="Source conflicts" items={review.conflictingFields} tone="conflict" emptyText="No source conflicts reported." />
              <IssuePanel title="Warnings" items={review.warnings} tone="warning" emptyText="No additional warnings reported." />
              <IssuePanel title="Clarification questions" items={review.clarificationQuestions} tone="question" emptyText="No clarification questions reported." />
            </div>

            <section className="uun-ci-duplicates" aria-labelledby="uun-ci-duplicates-title">
              <h3 id="uun-ci-duplicates-title">Possible duplicates</h3>
              {review.duplicateSuggestions.length ? (
                <ul>
                  {review.duplicateSuggestions.map((duplicate, index) => (
                    <li key={duplicate.eventId || duplicate.id || index}>
                      <strong>
                        {duplicate.title ||
                          duplicate.eventTitle ||
                          duplicate.eventId ||
                          duplicate.id ||
                          "Existing event"}
                      </strong>
                      {duplicate.date || duplicate.startDate ? (
                        <span>{duplicate.date || duplicate.startDate}</span>
                      ) : duplicate.classification ? (
                        <span>
                          {duplicate.classification.replace(/_/g, " ")}
                        </span>
                      ) : null}
                      <p>{duplicateReason(duplicate)}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="uun-ci-muted">No possible duplicates were suggested.</p>
              )}
              <p className="uun-ci-muted">Suggestions are advisory. Similar recurring sessions are not merged automatically.</p>
            </section>
          </section>

          <div className="uun-ci-review-grid">
            <section className="uun-ci-card" aria-labelledby="uun-ci-message-title">
              <div className="uun-ci-section-heading">
                <div>
                  <p className="uun-ci-step">Clarify</p>
                  <h2 id="uun-ci-message-title">Copy-only clarification message</h2>
                </div>
              </div>
              <textarea
                className="uun-ci-message"
                value={review.clarificationMessage}
                readOnly
                rows="9"
                aria-label="Clarification message draft"
              />
              <div className="uun-ci-actions">
                <button type="button" className="uun-ci-button uun-ci-button-secondary" onClick={copyMessage} disabled={!review.clarificationMessage || Boolean(busyAction)}>
                  Copy clarification message
                </button>
                {!approved ? (
                  <button type="button" className="uun-ci-button uun-ci-button-secondary" onClick={markNeedsInformation} disabled={dirty || !review.clarificationMessage || Boolean(busyAction)}>
                    {busyAction === "needs-information" ? "Marking…" : "Mark needs information"}
                  </button>
                ) : null}
              </div>
              <p className="uun-ci-muted">This tool never sends the message.</p>
            </section>

            <section className="uun-ci-card" aria-labelledby="uun-ci-preview-title">
              <div className="uun-ci-section-heading">
                <div>
                  <p className="uun-ci-step">Preview</p>
                  <h2 id="uun-ci-preview-title">Public event card</h2>
                </div>
              </div>
              <div className="uun-ci-public-preview">
                <EventCard event={previewEvent} variant="compact" flyerEager />
              </div>
              <p className="uun-ci-muted">Preview only. The original flyer reference is reused; no duplicate upload is created.</p>
            </section>
          </div>

          {!approved ? (
            <section className="uun-ci-card uun-ci-final-actions" aria-labelledby="uun-ci-approval-title">
              <div>
                <p className="uun-ci-step">Final decision</p>
                <h2 id="uun-ci-approval-title">Administrator approval</h2>
                <p>
                   {dirty
                     ? "Save your edits before approval."
                     : blockers.length
                     ? "Resolve the reported publication blockers and save the rechecked draft before approval."
                     : !approvalReady
                     ? "Edit and save the review before returning it to the approval-ready state."
                     : "The server will recheck the saved review version before creating or updating the one canonical public event."}
                </p>
              </div>
              <div className="uun-ci-actions">
                <button type="button" className="uun-ci-button uun-ci-button-danger" onClick={rejectDraft} disabled={Boolean(busyAction)}>
                  {busyAction === "reject" ? "Rejecting…" : "Reject suggestion / abandon"}
                </button>
                {!confirmApproval ? (
                  <button type="button" className="uun-ci-button uun-ci-button-primary" onClick={() => setConfirmApproval(true)} disabled={!approvalReady || dirty || blockers.length > 0 || Boolean(busyAction)}>
                    Approve / publish
                  </button>
                ) : (
                  <div className="uun-ci-confirm">
                    <strong>Publish this saved draft?</strong>
                    <button type="button" className="uun-ci-button uun-ci-button-primary" onClick={approveDraft} disabled={Boolean(busyAction)}>
                      {busyAction === "approve" ? "Publishing…" : "Confirm approve and publish"}
                    </button>
                    <button type="button" className="uun-ci-button uun-ci-button-quiet" onClick={() => setConfirmApproval(false)} disabled={Boolean(busyAction)}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </section>
          ) : (
            <div className="uun-ci-alert uun-ci-alert-success" role="status">This reviewed draft is approved. The existing public event workflow remains the source of truth.</div>
          )}
        </>
      ) : null}
    </section>
  );
}
